import { useState, useEffect } from 'react';
import {
    UserPlusIcon,
    MagnifyingGlassIcon,
    PencilSquareIcon,
    TrashIcon,
    UsersIcon,
    UserGroupIcon,
    ShieldCheckIcon,
    ClockIcon,
    CheckCircleIcon,
    XCircleIcon,
    ArchiveBoxIcon,
    ArrowPathIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { auth, conductores as conductoresApi } from '../services/api';
import Modal from '../components/Modal';
import ConfirmModal from '../components/ConfirmModal';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { format, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

export default function UsersPage() {
    const toast = useToast();
    const { user: currentUser } = useAuth();

    // Data state
    const [users, setUsers] = useState([]);
    const [deletedUsers, setDeletedUsers] = useState([]);
    const [stats, setStats] = useState(null);
    const [allConductors, setAllConductors] = useState([]);
    const [loading, setLoading] = useState(true);

    // UI state
    const [activeTab, setActiveTab] = useState('active'); // 'active' or 'trash'
    const [searchQuery, setSearchQuery] = useState('');
    const [filterRole, setFilterRole] = useState('all');
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
    const [selectedUser, setSelectedUser] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // Delete confirmation state
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);

    // Purge confirmation state
    const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);
    const [userToPurge, setUserToPurge] = useState(null);
    const [revokeConfirm, setRevokeConfirm] = useState({ isOpen: false, userId: null, nombre: '' });

    // Form state
    const [formData, setFormData] = useState({
        nombre: '',
        email: '',
        password: '',
        role: 'viewer',
        employee_type: 'none',
        conductor_id: '',
        activo: true
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [usersRes, statsRes, conductorsRes, deletedRes] = await Promise.all([
                auth.getUsers(),
                auth.getStats(),
                conductoresApi.getAll(),
                auth.getDeletedUsers()
            ]);
            setUsers(usersRes.users || []);
            setStats(statsRes);
            setAllConductors(conductorsRes || []);
            setDeletedUsers(deletedRes.users || []);
        } catch (error) {
            console.error(error);
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setModalMode('create');
        setFormData({
            nombre: '',
            email: '',
            password: '',
            role: 'viewer',
            employee_type: 'none',
            conductor_id: '',
            activo: true
        });
        setShowModal(true);
    };

    const handleEdit = (user) => {
        setModalMode('edit');
        setSelectedUser(user);
        setFormData({
            nombre: user.nombre,
            email: user.email,
            password: '', // Password empty on edit unless update
            role: user.role,
            employee_type: user.employee_type || 'none',
            conductor_id: user.conductor_id || '',
            activo: Boolean(user.activo)
        });
        setShowModal(true);
    };

    const handleDelete = (user) => {
        setUserToDelete(user);
        setShowDeleteConfirm(true);
    };

    const handleRevoke = (id, nombre) => {
        setRevokeConfirm({ isOpen: true, userId: id, nombre });
    };

    const confirmRevoke = async () => {
        if (!revokeConfirm.userId) return;
        try {
            await auth.revokeUserSessions(revokeConfirm.userId);
            toast.success('Sesiones revocadas exitosamente');
        } catch (error) {
            console.error(error);
            toast.error(error.message || 'Error al revocar sesiones');
        }
    };

    // UI button addition after delete button
    // (Will be inserted in the JSX below where actions are rendered)


    const confirmDelete = async () => {
        if (!userToDelete) return;

        try {
            await auth.deleteUser(userToDelete.id);
            toast.success('Usuario movido a la papelera');
            setShowDeleteConfirm(false);
            setUserToDelete(null);
            loadData();
        } catch (error) {
            toast.error(error.message || 'Error al eliminar usuario');
        }
    };

    const handleRestore = async (user) => {
        try {
            await auth.restoreUser(user.id);
            toast.success('Usuario restaurado correctamente');
            loadData();
        } catch (error) {
            toast.error(error.message || 'Error al restaurar usuario');
        }
    };

    const handlePurge = (user) => {
        setUserToPurge(user);
        setShowPurgeConfirm(true);
    };

    const confirmPurge = async () => {
        if (!userToPurge) return;

        try {
            await auth.purgeUser(userToPurge.id);
            toast.success('Usuario eliminado permanentemente');
            setShowPurgeConfirm(false);
            setUserToPurge(null);
            loadData();
        } catch (error) {
            toast.error(error.message || 'Error al purgar usuario');
        }
    };

    const handleSubmit = async () => {
        if (!formData.nombre || !formData.email) {
            toast.error('Nombre y Email son obligatorios');
            return;
        }
        if (modalMode === 'create' && !formData.password) {
            toast.error('Contraseña es obligatoria para nuevos usuarios');
            return;
        }

        setSubmitting(true);
        try {
            if (modalMode === 'create') {
                await auth.register(formData);
                toast.success('Usuario creado correctamente');
            } else {
                await auth.updateUser(selectedUser.id, formData);
                toast.success('Usuario actualizado correctamente');
            }

            setShowModal(false);
            setUsers([]);
            await loadData();

        } catch (error) {
            console.error('Error in handleSubmit:', error);
            toast.error(error.message || 'Error al guardar usuario');
        } finally {
            setSubmitting(false);
        }
    };

    // Filtering
    const filteredUsers = users.filter(user => {
        const matchesSearch =
            user.nombre.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.email.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesRole = filterRole === 'all' || user.role === filterRole;
        return matchesSearch && matchesRole;
    });

    const roles = [
        { value: 'admin', label: 'Administrador', color: 'red' },
        { value: 'conductor', label: 'Conductor', color: 'cyan' },
        { value: 'inspector', label: 'Inspector', color: 'purple' },
        { value: 'mecanico', label: 'Mecánico', color: 'orange' },
        { value: 'administrativo', label: 'Administrativo', color: 'green' },
        { value: 'viewer', label: 'Visualizador', color: 'gray' }
    ];

    const employeeTypes = [
        { value: 'none', label: 'Ninguno' },
        { value: 'conductor', label: 'Conductor' },
        { value: 'inspector', label: 'Inspector' },
        { value: 'mecanico', label: 'Mecánico' },
        { value: 'administrativo', label: 'Administrativo' }
    ];

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="w-8 h-8 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                        <UserGroupIcon className="w-8 h-8 text-orange-500" />
                        Gestión de Usuarios
                    </h1>
                    <p className="text-gray-400 mt-1 text-sm sm:text-base">
                        Administra permisos, roles y accesos al sistema
                    </p>
                </div>
                <button
                    onClick={handleCreate}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-orange-500 to-amber-600 text-white font-bold rounded-xl shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 transition-all active:scale-95"
                >
                    <UserPlusIcon className="w-5 h-5" />
                    Nuevo Usuario
                </button>
            </div>

            {/* Stats Cards */}
            {stats && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-[#1a1a3e]/50 border border-white/10 rounded-xl p-4 flex items-center gap-4">
                        <div className="p-3 bg-blue-500/20 rounded-lg">
                            <UsersIcon className="w-6 h-6 text-blue-400" />
                        </div>
                        <div>
                            <p className="text-gray-400 text-xs uppercase tracking-wider">Total Usuarios</p>
                            <p className="text-2xl font-bold text-white">{stats.total}</p>
                        </div>
                    </div>
                    <div className="bg-[#1a1a3e]/50 border border-white/10 rounded-xl p-4 flex items-center gap-4">
                        <div className="p-3 bg-green-500/20 rounded-lg">
                            <CheckCircleIcon className="w-6 h-6 text-green-400" />
                        </div>
                        <div>
                            <p className="text-gray-400 text-xs uppercase tracking-wider">Activos</p>
                            <p className="text-2xl font-bold text-white">{stats.active}</p>
                        </div>
                    </div>
                    <div className="bg-[#1a1a3e]/50 border border-white/10 rounded-xl p-4 flex items-center gap-4">
                        <div className="p-3 bg-red-500/20 rounded-lg">
                            <ShieldCheckIcon className="w-6 h-6 text-red-400" />
                        </div>
                        <div>
                            <p className="text-gray-400 text-xs uppercase tracking-wider">Admins</p>
                            <p className="text-2xl font-bold text-white">
                                {stats.byRole.find(r => r.role === 'admin')?.count || 0}
                            </p>
                        </div>
                    </div>
                    <div className="bg-[#1a1a3e]/50 border border-white/10 rounded-xl p-4 flex items-center gap-4">
                        <div className="p-3 bg-purple-500/20 rounded-lg">
                            <ClockIcon className="w-6 h-6 text-purple-400" />
                        </div>
                        <div>
                            <p className="text-gray-400 text-xs uppercase tracking-wider">Logins (7 días)</p>
                            <p className="text-2xl font-bold text-white">{stats.recentLogins}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div className="flex gap-2">
                <button
                    onClick={() => setActiveTab('active')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${activeTab === 'active'
                        ? 'bg-orange-500/20 text-orange-500 border border-orange-500/30'
                        : 'bg-white/5 text-gray-400 hover:bg-white/10 border border-transparent'
                        }`}
                >
                    <UsersIcon className="w-4 h-4" />
                    Usuarios Activos
                    <span className="ml-1 px-2 py-0.5 bg-orange-500/20 rounded-full text-xs">
                        {users.length}
                    </span>
                </button>
                <button
                    onClick={() => setActiveTab('trash')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${activeTab === 'trash'
                        ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                        : 'bg-white/5 text-gray-400 hover:bg-white/10 border border-transparent'
                        }`}
                >
                    <ArchiveBoxIcon className="w-4 h-4" />
                    Papelera
                    {deletedUsers.length > 0 && (
                        <span className="ml-1 px-2 py-0.5 bg-orange-500/20 rounded-full text-xs">
                            {deletedUsers.length}
                        </span>
                    )}
                </button>
            </div>

            {activeTab === 'active' && (
                <>
                    <div className="bg-[#1a1a3e]/50 border border-white/10 rounded-xl p-4 flex flex-col sm:flex-row gap-4">
                        {/* Dummy inputs to capture autofill */}
                        <input type="text" name="dummy_email" style={{ display: 'none' }} disabled />
                        <input type="password" name="dummy_pass" style={{ display: 'none' }} disabled />

                        <div className="relative flex-1">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                            <input
                                type="text"
                                name="search_query_random_id"
                                id="search_query_random_id"
                                autoComplete="new-password"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Buscar por nombre o email..."
                                className="w-full pl-10 pr-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                            />
                        </div>
                        <select
                            value={filterRole}
                            onChange={(e) => setFilterRole(e.target.value)}
                            className="px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                        >
                            <option value="all">Todos los roles</option>
                            {roles.map(role => (
                                <option key={role.value} value={role.value}>{role.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Users Table */}
                    <div className="bg-[#1a1a3e]/50 border border-white/10 rounded-xl overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-white/10">
                                        <th className="text-left text-gray-400 text-sm font-medium p-3 sm:p-4">Usuario</th>
                                        <th className="text-left text-gray-400 text-sm font-medium p-3 sm:p-4 hidden sm:table-cell">Rol / Tipo</th>
                                        <th className="text-left text-gray-400 text-sm font-medium p-3 sm:p-4 hidden md:table-cell">Estado</th>
                                        <th className="text-left text-gray-400 text-sm font-medium p-3 sm:p-4 hidden lg:table-cell">Último Acceso</th>
                                        <th className="text-right text-gray-400 text-sm font-medium p-3 sm:p-4">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredUsers.length === 0 ? (
                                        <tr>
                                            <td colSpan="5" className="text-center text-gray-500 py-12">
                                                No se encontraron usuarios
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredUsers.map(user => (
                                            <tr key={user.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                                                <td className="p-3 sm:p-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center text-white font-medium border border-white/10 flex-shrink-0">
                                                            {user.nombre.charAt(0).toUpperCase()}
                                                        </div>
                                                        <div className="min-w-0">
                                                            <p className="font-medium text-white truncate max-w-[120px] sm:max-w-none">{user.nombre}</p>
                                                            <p className="text-xs sm:text-sm text-gray-400 truncate max-w-[120px] sm:max-w-none">{user.email}</p>
                                                            {/* Mobile only role badge */}
                                                            <div className="sm:hidden mt-1">
                                                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${user.role === 'admin'
                                                                    ? 'bg-red-500/10 text-red-400 border-red-500/20'
                                                                    : 'bg-gray-500/10 text-gray-400 border-gray-500/20'
                                                                    }`}>
                                                                    {roles.find(r => r.value === user.role)?.label || user.role}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="p-3 sm:p-4 hidden sm:table-cell">
                                                    <div className="flex flex-col gap-1 items-start">
                                                        <span className={`px-2 py-0.5 rounded text-xs font-medium border ${user.role === 'admin'
                                                            ? 'bg-red-500/10 text-red-400 border-red-500/20'
                                                            : 'bg-gray-500/10 text-gray-400 border-gray-500/20'
                                                            }`}>
                                                            {roles.find(r => r.value === user.role)?.label || user.role}
                                                        </span>
                                                        {user.employee_type !== 'none' && (
                                                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-orange-500/10 text-orange-500 border border-orange-500/20">
                                                                {user.employee_type}
                                                                {user.conductor_nombre && ` (${user.conductor_nombre})`}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="p-3 sm:p-4 hidden md:table-cell">
                                                    {user.activo ? (
                                                        <div className="flex items-center gap-2 text-green-400 text-sm">
                                                            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                                                            Activo
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-2 text-gray-500 text-sm">
                                                            <div className="w-2 h-2 rounded-full bg-gray-500"></div>
                                                            Inactivo
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="p-3 sm:p-4 text-gray-400 text-sm hidden lg:table-cell">
                                                    {user.last_login
                                                        ? format(new Date(user.last_login), 'dd MMM yyyy HH:mm', { locale: es })
                                                        : <span className="text-gray-600">Nunca</span>
                                                    }
                                                </td>
                                                <td className="p-4 text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <button
                                                            onClick={() => handleEdit(user)}
                                                            className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                                                            title="Editar"
                                                        >
                                                            <PencilSquareIcon className="w-5 h-5" />
                                                        </button>
                                                        {user.id !== currentUser.id && (
                                                            <>
                                                                <button
                                                                    onClick={() => handleDelete(user)}
                                                                    className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                                                    title="Eliminar"
                                                                >
                                                                    <TrashIcon className="w-5 h-5" />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleRevoke(user.id, user.nombre)}
                                                                    className="p-2 text-yellow-400 hover:bg-yellow-400/10 rounded-lg transition-colors"
                                                                    title="Revocar sesión"
                                                                >
                                                                    <ArrowPathIcon className="w-5 h-5" />
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}

            {/* Trash Tab */}
            {activeTab === 'trash' && (
                <div className="bg-[#1a1a3e]/50 border border-white/10 rounded-xl overflow-hidden">
                    {deletedUsers.length === 0 ? (
                        <div className="text-center py-12">
                            <ArchiveBoxIcon className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                            <p className="text-gray-400">La papelera está vacía</p>
                            <p className="text-gray-500 text-sm mt-1">Los usuarios eliminados aparecerán aquí</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-white/10">
                                        <th className="text-left text-gray-400 text-sm font-medium p-4">Usuario</th>
                                        <th className="text-left text-gray-400 text-sm font-medium p-4">Eliminado</th>
                                        <th className="text-left text-gray-400 text-sm font-medium p-4">Por</th>
                                        <th className="text-right text-gray-400 text-sm font-medium p-4">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {deletedUsers.map(user => (
                                        <tr key={user.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                                            <td className="p-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center text-white font-medium border border-white/10 opacity-50">
                                                        {user.nombre.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-gray-400">{user.nombre}</p>
                                                        <p className="text-sm text-gray-500">{user.email}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="p-4">
                                                <div className="text-orange-400 text-sm">
                                                    {formatDistanceToNow(new Date(user.deleted_at), { addSuffix: true, locale: es })}
                                                </div>
                                                <div className="text-gray-500 text-xs">
                                                    {format(new Date(user.deleted_at), 'dd MMM yyyy HH:mm', { locale: es })}
                                                </div>
                                            </td>
                                            <td className="p-4 text-gray-400 text-sm">
                                                {user.deleted_by_nombre || 'Sistema'}
                                            </td>
                                            <td className="p-4 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={() => handleRestore(user)}
                                                        className="flex items-center gap-1 px-3 py-1.5 text-green-400 hover:bg-green-500/10 rounded-lg transition-colors text-sm"
                                                        title="Restaurar"
                                                    >
                                                        <ArrowPathIcon className="w-4 h-4" />
                                                        Restaurar
                                                    </button>
                                                    <button
                                                        onClick={() => handlePurge(user)}
                                                        className="flex items-center gap-1 px-3 py-1.5 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors text-sm"
                                                        title="Eliminar permanentemente"
                                                    >
                                                        <TrashIcon className="w-4 h-4" />
                                                        Purgar
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Info sobre retención */}
                    <div className="border-t border-white/10 p-4 bg-white/5">
                        <div className="flex items-start gap-3">
                            <ExclamationTriangleIcon className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                            <div className="text-sm">
                                <p className="text-gray-300">Los usuarios eliminados se pueden restaurar en cualquier momento.</p>
                                <p className="text-gray-500 mt-1">Después de 2 años, puedes purgarlos definitivamente para cumplir con LOPD/GDPR.</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Create/Edit Modal */}
            <Modal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                title={modalMode === 'create' ? 'Crear Nuevo Usuario' : 'Editar Usuario'}
                footer={
                    <>
                        <button
                            type="button"
                            onClick={() => setShowModal(false)}
                            className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={submitting}
                            className="px-6 py-2 bg-gradient-to-r from-orange-500 to-amber-600 text-white font-medium rounded-lg disabled:opacity-50"
                        >
                            {submitting ? 'Guardando...' : 'Guardar'}
                        </button>
                    </>
                }
            >
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Nombre</label>
                        <input
                            type="text"
                            value={formData.nombre}
                            onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                            className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                            placeholder="Nombre completo"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
                        <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            disabled={modalMode === 'edit'}
                            className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
                            placeholder="correo@ejemplo.com"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                            {modalMode === 'create' ? 'Contraseña' : 'Contraseña (dejar en blanco para no cambiar)'}
                        </label>
                        <input
                            type="password"
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                            placeholder="••••••••"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Rol Sistema</label>
                            <select
                                value={formData.role}
                                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                            >
                                {roles.map(role => (
                                    <option key={role.value} value={role.value}>{role.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Tipo Empleado</label>
                            <select
                                value={formData.employee_type}
                                onChange={(e) => setFormData({ ...formData, employee_type: e.target.value })}
                                className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                            >
                                {employeeTypes.map(type => (
                                    <option key={type.value} value={type.value}>{type.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                            Vincular Conductor (Apodo)
                        </label>
                        <select
                            value={formData.conductor_id}
                            onChange={(e) => {
                                const newConductorId = e.target.value;
                                setFormData({
                                    ...formData,
                                    conductor_id: newConductorId,
                                    employee_type: (newConductorId && formData.employee_type === 'none')
                                        ? 'conductor'
                                        : formData.employee_type
                                });
                            }}
                            className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                        >
                            <option value="">-- Sin vincular --</option>
                            {allConductors.map(c => (
                                <option key={c.id} value={c.id}>
                                    {c.nombre} (ID: {c.id})
                                </option>
                            ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                            Vincula este usuario a un perfil de conductor/empleado existente. Se actualizará el tipo a "Conductor" automáticamente si es necesario.
                        </p>
                    </div>

                    <div className="flex items-center gap-2 pt-2">
                        <input
                            type="checkbox"
                            id="activo"
                            checked={formData.activo}
                            onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                            className="w-4 h-4 rounded border-gray-600 text-cyan-500 focus:ring-orange-500/50 bg-black/40"
                        />
                        <label htmlFor="activo" className="text-gray-300 text-sm">
                            Usuario Activo
                        </label>
                    </div>
                </div>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={showDeleteConfirm}
                onClose={() => {
                    setShowDeleteConfirm(false);
                    setUserToDelete(null);
                }}
                title="Eliminar Usuario"
                size="sm"
                footer={
                    <>
                        <button
                            type="button"
                            onClick={() => {
                                setShowDeleteConfirm(false);
                                setUserToDelete(null);
                            }}
                            className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            onClick={confirmDelete}
                            className="px-6 py-2 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg transition-colors"
                        >
                            Mover a Papelera
                        </button>
                    </>
                }
            >
                <div className="text-center py-4">
                    <div className="w-16 h-16 mx-auto mb-4 bg-orange-500/20 rounded-full flex items-center justify-center">
                        <ArchiveBoxIcon className="w-8 h-8 text-orange-400" />
                    </div>
                    <p className="text-white text-lg mb-2">
                        ¿Eliminar a {userToDelete?.nombre}?
                    </p>
                    <p className="text-gray-400 text-sm">
                        El usuario será movido a la papelera y podrá ser restaurado más adelante.
                    </p>
                </div>
            </Modal>

            {/* Purge Confirmation Modal */}
            <Modal
                isOpen={showPurgeConfirm}
                onClose={() => {
                    setShowPurgeConfirm(false);
                    setUserToPurge(null);
                }}
                title="Eliminar Permanentemente"
                size="sm"
                footer={
                    <>
                        <button
                            type="button"
                            onClick={() => {
                                setShowPurgeConfirm(false);
                                setUserToPurge(null);
                            }}
                            className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            onClick={confirmPurge}
                            className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
                        >
                            Purgar Definitivamente
                        </button>
                    </>
                }
            >
                <div className="text-center py-4">
                    <div className="w-16 h-16 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
                        <ExclamationTriangleIcon className="w-8 h-8 text-red-400" />
                    </div>
                    <p className="text-white text-lg mb-2">
                        ¿Purgar a {userToPurge?.nombre}?
                    </p>
                    <p className="text-red-400 text-sm font-medium mb-2">
                        ⚠️ Esta acción NO se puede deshacer
                    </p>
                    <p className="text-gray-400 text-sm">
                        El usuario y sus referencias serán eliminados permanentemente de la base de datos.
                    </p>
                </div>
            </Modal>

            <ConfirmModal
                isOpen={revokeConfirm.isOpen}
                onClose={() => setRevokeConfirm({ isOpen: false, userId: null, nombre: '' })}
                onConfirm={confirmRevoke}
                title="Revocar sesiones"
                message={`¿Revocar todas las sesiones del usuario "${revokeConfirm.nombre}"?`}
                confirmText="Revocar"
                variant="warning"
            />
        </div>
    );
}

